import { getAppDimensions } from "..";
import Scenes from "../enums/Scenes";
import StageType from "../enums/StageType";
import Game from "../game/Game";
import GameCore from "../game/GameCore";
import SceneManager from "../game/SceneManager";
// import CharacterManagementScene from './characterManagementScene'
import { LOOKS_SPRITES } from "../consts/LOOKS_SPRITES";
import { FontManager } from "../../utils/FontManager";
import EvolutionScene from "./EvolutionScene";
import { SPRITESHEET_KEYS, SPRITESHEET_FRAMES } from "../consts/SPRITESHEETS";

export default class PreloadScene extends Phaser.Scene {
  constructor() {
    super({ key: Scenes.PreloadScene });
  }

  preload() {
    // Show loading screen when asset loading begins
    if (typeof (window as any).showLoadingScreen === "function") {
      (window as any).showLoadingScreen();
    }
    this.load.image("bgTile", "assets/img/desert_map.webp");
    this.load.image("bgTileGreen", "assets/img/green_map.webp");
    this.load.image(
      "desert_background",
      "assets/img/levels/desert_background.webp"
    );
    this.load.image(
      "city_background",
      "assets/img/levels/city_background.webp"
    );
    this.load.image(
      "jungle_background",
      "assets/img/levels/jungle_background.webp"
    );
    this.load.image(
      "cyberpunk_background",
      "assets/img/levels/cyberpunk_background.webp"
    );
    this.load.image(
      "metaverse_background",
      "assets/img/levels/metaverse_background.webp"
    );

    //UI
    this.load.image("explosion_icon", "assets/img/ui/explosion.webp");
    this.load.image("heal_icon", "assets/img/ui/heal.webp");
    this.load.image("energy_full", "assets/img/ui/energy_full.webp");
    this.load.image("energy_empty", "assets/img/ui/energy_empty.webp");

    //character preloads
    Object.values(LOOKS_SPRITES).forEach((spriteKey) => {
      this.load.spritesheet(
        spriteKey,
        `assets/img/characters/${spriteKey}.webp`,
        {
          frameWidth: 48,
          frameHeight: 48,
        }
      );
    });

    //camel preload
    this.load.spritesheet("camel", "assets/img/camel.webp", {
      frameWidth: 135,
      frameHeight: 103,
      spacing: 21,
    });

    //enemies preloads
    // All character enemies now loaded from difficulty-based atlases
    // Individual enemy files are no longer needed

    // Load spritesheets instead of individual images
    this.load.atlas(
      SPRITESHEET_KEYS.PICKUPS,
      "assets/img/spritesheets/pickups.png",
      "assets/img/spritesheets/pickups.json"
    );

    this.load.spritesheet("enemy_death", "assets/img/enemy_death.webp", {
      frameWidth: 64,
      frameHeight: 64,
      startFrame: 7,
      endFrame: 11,
    });

    // Load weapon effects spritesheet (contains animation frames)
    this.load.atlas(
      SPRITESHEET_KEYS.WEAPON_EFFECTS,
      "assets/img/spritesheets/weapon_effects.png",
      "assets/img/spritesheets/weapon_effects.json"
    );

    // Load weapons spritesheet
    this.load.atlas(
      SPRITESHEET_KEYS.WEAPONS,
      "assets/img/spritesheets/weapons.png",
      "assets/img/spritesheets/weapons.json"
    );

    // Load stats spritesheet
    this.load.atlas(
      SPRITESHEET_KEYS.STATS,
      "assets/img/spritesheets/stats.png",
      "assets/img/spritesheets/stats.json"
    );

    // Load enemy spritesheets by difficulty
    this.load.atlas(
      SPRITESHEET_KEYS.EASY_ENEMIES,
      "assets/img/spritesheets/easy_enemies.png",
      "assets/img/spritesheets/easy_enemies.json"
    );

    this.load.atlas(
      SPRITESHEET_KEYS.MEDIUM_ENEMIES,
      "assets/img/spritesheets/medium_enemies.png",
      "assets/img/spritesheets/medium_enemies.json"
    );

    this.load.atlas(
      SPRITESHEET_KEYS.DIFFICULT_ENEMIES,
      "assets/img/spritesheets/difficult_enemies.png",
      "assets/img/spritesheets/difficult_enemies.json"
    );

    this.load.atlas(
      SPRITESHEET_KEYS.BOSS_ENEMIES,
      "assets/img/spritesheets/boss_enemies.png",
      "assets/img/spritesheets/boss_enemies.json"
    );

    this.load.atlas(
      SPRITESHEET_KEYS.GREEN_ENEMIES,
      "assets/img/spritesheets/green_enemies.png",
      "assets/img/spritesheets/green_enemies.json"
    );

    this.load.atlas(
      SPRITESHEET_KEYS.GREEN_BOSSES_ENEMIES,
      "assets/img/spritesheets/green_bosses_enemies.png",
      "assets/img/spritesheets/green_bosses_enemies.json"
    );

    //NPC images
    this.load.image("old_man", "assets/img/npc/old_man.webp");

    //this.extractNarrationPortraits();

    this.load.audio("desert_music", "assets/audio/desert_music.m4a");
    this.load.audio("levelup_music", [
      "assets/audio/levelup.ogg",
      "assets/audio/levelup.m4a",
    ]);
    this.load.audio("treasure_music", [
      "assets/audio/treasure.ogg",
      "assets/audio/treasure.m4a",
    ]);
    this.load.audio("kill_sound", [
      "assets/audio/kill.ogg",
      "assets/audio/kill.m4a",
    ]);
    this.load.audio("coin_sound", [
      "assets/audio/pickup.ogg",
      "assets/audio/pickup.m4a",
    ]);
    this.load.audio("apple_sound", [
      "assets/audio/apple.ogg",
      "assets/audio/apple.m4a",
    ]);
    this.load.audio("explode_sound", [
      "assets/audio/explode.ogg",
      "assets/audio/explode.m4a",
    ]);
    this.load.audio("gameover_sound", [
      "assets/audio/gameover.ogg",
      "assets/audio/gameover.m4a",
    ]);
  }

  async create() {
    try {
      await FontManager.loadFonts();
    } catch (error) {
      console.error("Failed to load fonts:", error);
    }

    const { APP_WIDTH, APP_HEIGHT } = getAppDimensions();
    this.scale.displaySize.setAspectRatio(APP_WIDTH / APP_HEIGHT);
    this.scale.resize(APP_WIDTH, APP_HEIGHT);
    this.scale.refresh();

    this.cameras.main.setBackgroundColor("#eee");

    Game.Core = new GameCore(this.game, this);
    await Game.Core.initialize();
    Game.Core.SceneManager = new SceneManager(this.game, this.scene);
    Game.Core.SceneManager.Init();
    Game.Core.PlayerOptions.Load();

    // Initialize the router to handle URL-based navigation
    if (Game.Core.SceneManager.router) {
      Game.Core.SceneManager.router.init();
    }

    //animate characters
    Object.values(LOOKS_SPRITES).forEach((spriteKey) => {
      this.anims.create({
        key: `${spriteKey}_run`,
        frames: this.anims.generateFrameNumbers(spriteKey, {
          frames: [24, 25, 26, 25],
        }),
        frameRate: 6,
        repeat: -1,
      });
    });

    // animate camel
    this.anims.create({
      key: "camel_run",
      frames: this.anims.generateFrameNumbers("camel", {
        frames: [0, 1, 2, 1],
      }),
      frameRate: 6,
      repeat: -1,
    });

    // animate enemies - now using difficulty-based atlases
    // Easy enemies
    this.anims.create({
      key: "small_hamster1_run",
      frames: this.anims.generateFrameNames(SPRITESHEET_KEYS.EASY_ENEMIES, {
        prefix: "small_hamster1_",
        start: 0,
        end: 3,
      }),
      frameRate: 6,
      repeat: -1,
    });
    this.anims.create({
      key: "small_hamster2_run",
      frames: this.anims.generateFrameNames(SPRITESHEET_KEYS.EASY_ENEMIES, {
        prefix: "small_hamster2_",
        start: 0,
        end: 4,
      }),
      frameRate: 6,
      repeat: -1,
    });
    this.anims.create({
      key: "fat_hamster_run",
      frames: this.anims.generateFrameNames(SPRITESHEET_KEYS.EASY_ENEMIES, {
        prefix: "fat_hamster_",
        start: 0,
        end: 5,
      }),
      frameRate: 6,
      repeat: -1,
    });
    this.anims.create({
      key: "warrior_hamster_run",
      frames: this.anims.generateFrameNames(SPRITESHEET_KEYS.EASY_ENEMIES, {
        prefix: "warrior_hamster_",
        start: 0,
        end: 4,
      }),
      frameRate: 6,
      repeat: -1,
    });
    this.anims.create({
      key: "scorpion_run",
      frames: this.anims.generateFrameNames(SPRITESHEET_KEYS.EASY_ENEMIES, {
        prefix: "scorpion_",
        start: 0,
        end: 2,
      }),
      frameRate: 8,
      repeat: -1,
    });
    this.anims.create({
      key: "snake1_run",
      frames: this.anims.generateFrameNames(SPRITESHEET_KEYS.EASY_ENEMIES, {
        prefix: "snake1_",
        frames: [0, 1, 2, 1],
      }),
      frameRate: 8,
      repeat: -1,
    });
    this.anims.create({
      key: "snake2_run",
      frames: this.anims.generateFrameNames(SPRITESHEET_KEYS.EASY_ENEMIES, {
        prefix: "snake2_",
        frames: [0, 1, 2, 1],
      }),
      frameRate: 8,
      repeat: -1,
    });
    this.anims.create({
      key: "cactus_run",
      frames: this.anims.generateFrameNames(SPRITESHEET_KEYS.EASY_ENEMIES, {
        prefix: "cactus_",
        frames: [0, 1, 2, 1],
      }),
      frameRate: 8,
      repeat: -1,
    });

    // Medium enemies
    this.anims.create({
      key: "vulture_run",
      frames: this.anims.generateFrameNames(SPRITESHEET_KEYS.MEDIUM_ENEMIES, {
        prefix: "vulture_",
        frames: [0, 1, 2, 1],
      }),
      frameRate: 8,
      repeat: -1,
    });
    this.anims.create({
      key: "golem_run",
      frames: this.anims.generateFrameNames(SPRITESHEET_KEYS.MEDIUM_ENEMIES, {
        prefix: "golem_",
        frames: [0, 1, 2, 1],
      }),
      frameRate: 8,
      repeat: -1,
    });

    // Difficult enemies
    this.anims.create({
      key: "reptile_run",
      frames: this.anims.generateFrameNames(
        SPRITESHEET_KEYS.DIFFICULT_ENEMIES,
        {
          prefix: "reptile_",
          frames: [0, 1, 2, 1],
        }
      ),
      frameRate: 8,
      repeat: -1,
    });

    // Boss enemies
    this.anims.create({
      key: "suit_hamster_run",
      frames: this.anims.generateFrameNames(SPRITESHEET_KEYS.BOSS_ENEMIES, {
        prefix: "suit_hamster_",
        start: 0,
        end: 5,
      }),
      frameRate: 6,
      repeat: -1,
    });
    this.anims.create({
      key: "lizard_hamster_run",
      frames: this.anims.generateFrameNames(SPRITESHEET_KEYS.BOSS_ENEMIES, {
        prefix: "lizard_hamster_",
        start: 0,
        end: 2,
      }),
      frameRate: 6,
      repeat: -1,
    });
    this.anims.create({
      key: "vampire_hamster_run",
      frames: this.anims.generateFrameNames(SPRITESHEET_KEYS.BOSS_ENEMIES, {
        prefix: "vampire_hamster_",
        start: 0,
        end: 4,
      }),
      frameRate: 6,
      repeat: -1,
    });
    this.anims.create({
      key: "king_hamster_run",
      frames: this.anims.generateFrameNames(SPRITESHEET_KEYS.BOSS_ENEMIES, {
        prefix: "king_hamster_",
        start: 0,
        end: 5,
      }),
      frameRate: 6,
      repeat: -1,
    });
    this.anims.create({
      key: "dude_run",
      frames: this.anims.generateFrameNames(SPRITESHEET_KEYS.BOSS_ENEMIES, {
        prefix: "dude_",
        start: 0,
        end: 5,
      }),
      frameRate: 8,
      repeat: -1,
    });

    // Green enemies
    this.anims.create({
      key: "chicken_run",
      frames: this.anims.generateFrameNames(SPRITESHEET_KEYS.GREEN_ENEMIES, {
        prefix: "chicken_",
        frames: [0, 1, 2, 1],
      }),
      frameRate: 8,
      repeat: -1,
    });

    this.anims.create({
      key: "duck_run",
      frames: this.anims.generateFrameNames(SPRITESHEET_KEYS.GREEN_ENEMIES, {
        prefix: "duck_",
        frames: [0, 1, 2, 1],
      }),
      frameRate: 8,
      repeat: -1,
    });

    this.anims.create({
      key: "spider_run",
      frames: this.anims.generateFrameNames(SPRITESHEET_KEYS.GREEN_ENEMIES, {
        prefix: "spider_",
        frames: [0, 1, 2, 1],
      }),
      frameRate: 8,
      repeat: -1,
    });
    this.anims.create({
      key: "mouseman_run",
      frames: this.anims.generateFrameNames(SPRITESHEET_KEYS.GREEN_ENEMIES, {
        prefix: "mouseman_",
        frames: [0, 1, 2, 1],
      }),
      frameRate: 8,
      repeat: -1,
    });
    this.anims.create({
      key: "mouseman2_run",
      frames: this.anims.generateFrameNames(SPRITESHEET_KEYS.GREEN_ENEMIES, {
        prefix: "mouseman2_",
        frames: [0, 1, 2, 1],
      }),
      frameRate: 8,
      repeat: -1,
    });
    this.anims.create({
      key: "treant_run",
      frames: this.anims.generateFrameNames(SPRITESHEET_KEYS.GREEN_ENEMIES, {
        prefix: "treant_",
        frames: [0, 1, 2, 1],
      }),
      frameRate: 8,
      repeat: -1,
    });
    this.anims.create({
      key: "skeleton_run",
      frames: this.anims.generateFrameNames(SPRITESHEET_KEYS.GREEN_ENEMIES, {
        prefix: "skeleton_",
        frames: [0, 1, 2, 1],
      }),
      frameRate: 8,
      repeat: -1,
    });
    this.anims.create({
      key: "bee_run",
      frames: this.anims.generateFrameNames(SPRITESHEET_KEYS.GREEN_ENEMIES, {
        prefix: "bee_",
        frames: [0, 1, 2, 1],
      }),
      frameRate: 8,
      repeat: -1,
    });
    this.anims.create({
      key: "wolf_run",
      frames: this.anims.generateFrameNames(SPRITESHEET_KEYS.GREEN_ENEMIES, {
        prefix: "wolf_",
        frames: [0, 1, 2, 1],
      }),
      frameRate: 8,
      repeat: -1,
    });
    this.anims.create({
      key: "green_golem_run",
      frames: this.anims.generateFrameNames(SPRITESHEET_KEYS.GREEN_ENEMIES, {
        prefix: "green_golem_",
        frames: [0, 1, 2, 1],
      }),
      frameRate: 8,
      repeat: -1,
    });

    // Green bosses
    this.anims.create({
      key: "tiger_run",
      frames: this.anims.generateFrameNames(
        SPRITESHEET_KEYS.GREEN_BOSSES_ENEMIES,
        {
          prefix: "tiger_",
          frames: [0, 1, 2, 1],
        }
      ),
      frameRate: 8,
      repeat: -1,
    });
    this.anims.create({
      key: "gorilla_run",
      frames: this.anims.generateFrameNames(
        SPRITESHEET_KEYS.GREEN_BOSSES_ENEMIES,
        {
          prefix: "gorilla_",
          frames: [0, 1, 2, 1],
        }
      ),
      frameRate: 8,
      repeat: -1,
    });
    this.anims.create({
      key: "rhinoceros_run",
      frames: this.anims.generateFrameNames(
        SPRITESHEET_KEYS.GREEN_BOSSES_ENEMIES,
        {
          prefix: "rhinoceros_",
          frames: [0, 1, 2, 1],
        }
      ),
      frameRate: 8,
      repeat: -1,
    });
    this.anims.create({
      key: "dark_knight_run",
      frames: this.anims.generateFrameNames(
        SPRITESHEET_KEYS.GREEN_BOSSES_ENEMIES,
        {
          prefix: "dark_knight_",
          frames: [0, 1, 2, 1],
        }
      ),
      frameRate: 8,
      repeat: -1,
    });
    this.anims.create({
      key: "crocodile_run",
      frames: this.anims.generateFrameNames(
        SPRITESHEET_KEYS.GREEN_BOSSES_ENEMIES,
        {
          prefix: "crocodile_",
          frames: [0, 1, 2, 1],
        }
      ),
      frameRate: 8,
      repeat: -1,
    });
    this.anims.create({
      key: "lion_run",
      frames: this.anims.generateFrameNames(
        SPRITESHEET_KEYS.GREEN_BOSSES_ENEMIES,
        {
          prefix: "lion_",
          frames: [0, 1, 2, 1],
        }
      ),
      frameRate: 8,
      repeat: -1,
    });

    // Weapon effect animations using atlas frames
    this.anims.create({
      key: "hashblade_attack",
      frames: this.anims.generateFrameNames(SPRITESHEET_KEYS.WEAPON_EFFECTS, {
        prefix: "hashblade_single_",
        start: 0,
        end: 0,
      }),
      frameRate: 1,
      repeat: -1,
    });

    this.anims.create({
      key: "blast_attack",
      frames: this.anims.generateFrameNames(SPRITESHEET_KEYS.WEAPON_EFFECTS, {
        prefix: "blast_",
        start: 0,
        end: 5,
      }),
      frameRate: 8,
      repeat: -1,
    });

    this.anims.create({
      key: "explosion_animation",
      frames: this.anims.generateFrameNames(SPRITESHEET_KEYS.WEAPON_EFFECTS, {
        prefix: "explosion_",
        start: 0,
        end: 6,
      }),
      frameRate: 12,
      repeat: 0,
    });

    this.anims.create({
      key: "enemy_death_animation",
      frames: this.anims.generateFrameNumbers("enemy_death"),
      frameRate: 12,
      hideOnComplete: true,
    });

    // this.scene.add(Scenes.CharacterManagementScene, CharacterManagementScene)
    this.scene.add(Scenes.EvolutionScene, EvolutionScene);

    // Add a wrapper for sound playing that handles missing audio files gracefully
    const originalSoundManagerPlay = this.sound.play;
    this.sound.play = function (key, extra) {
      try {
        if (this.get(key)) {
          return originalSoundManagerPlay.call(this, key, extra);
        } else {
          console.warn(`Attempted to play missing sound: ${key}`);
          // Return false instead of null to match the expected boolean return type
          return false;
        }
      } catch (error) {
        console.warn(`Error playing sound ${key}:`, error);
        // Return false instead of null to match the expected boolean return type
        return false;
      }
    };

    // Check if a stage was selected from URL parameters
    const selectedStage = (window as any).selectedStageFromURL;
    if (selectedStage) {
      // Validate that the selected stage is a valid StageType
      const validStageValues = Object.values(StageType);
      if (validStageValues.includes(selectedStage)) {
        // Set the selected stage in PlayerOptions
        Game.Core.PlayerOptions.SelectedStage = selectedStage;
        // Clear the global variable
        delete (window as any).selectedStageFromURL;
        // Navigate to the main game scene
        Game.Core.SceneManager.NavigateToScene(Scenes.MainScene);
      } else {
        console.warn(
          `Invalid stage from URL: ${selectedStage}. Using default navigation.`
        );
        // Clear the invalid global variable
        delete (window as any).selectedStageFromURL;
        // Navigate to main menu with default behavior
        Game.Core.SceneManager.NavigateToScene(Scenes.MainMenuScene);
      }
    } else {
      // Default behavior - navigate to main menu
      Game.Core.SceneManager.NavigateToScene(Scenes.MainMenuScene);
    }
  }
}
